import { useState, useCallback, useRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { XMarkIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';

interface ChatCaptchaProps {
  isVisible: boolean;
  onVerified: (token: string) => void;
  onClose: () => void;
  onError?: (error: string) => void;
  // For invisible mode, we need to trigger verification programmatically
  shouldExecute?: boolean;
  onExecuteComplete?: () => void;
  // New prop to control when invisible badge should be shown
  isChatOpen?: boolean;
}

export default function ChatCaptcha({ 
  isVisible, 
  onVerified, 
  onClose, 
  onError,
  shouldExecute = false,
  onExecuteComplete,
  isChatOpen = false
}: ChatCaptchaProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  const version = import.meta.env.VITE_RECAPTCHA_VERSION || 'V2';
  const isInvisible = import.meta.env.VITE_RECAPTCHA_INVISIBLE === 'true';

  // For invisible reCAPTCHA, execute when shouldExecute changes
  useEffect(() => {
    if (isInvisible && shouldExecute && recaptchaRef.current) {
      setIsLoading(true);
      recaptchaRef.current.execute();
    }
  }, [shouldExecute, isInvisible]);

  const handleCaptchaChange = useCallback((token: string | null) => {
    setIsLoading(false);
    
    if (token) {
      setError(null);
      onVerified(token);
      
      // For invisible mode, call completion callback
      if (isInvisible && onExecuteComplete) {
        onExecuteComplete();
      }
    }
  }, [onVerified, isInvisible, onExecuteComplete]);

  const handleCaptchaError = useCallback(() => {
    setIsLoading(false);
    const errorMsg = 'CAPTCHA verification failed. Please try again.';
    setError(errorMsg);
    onError?.(errorMsg);
    
    if (isInvisible && onExecuteComplete) {
      onExecuteComplete();
    }
  }, [onError, isInvisible, onExecuteComplete]);

  const handleCaptchaExpired = useCallback(() => {
    setError('CAPTCHA expired. Please verify again.');
    setIsLoading(false);
  }, []);

  const handleManualExecute = useCallback(() => {
    if (isInvisible && recaptchaRef.current) {
      setIsLoading(true);
      setError(null);
      recaptchaRef.current.execute();
    }
  }, [isInvisible]);

  if (!siteKey) {
    return null;
  }

  // For invisible reCAPTCHA, render a minimal indicator only when chat is open
  if (isInvisible) {
    // Only show the badge when chat is open
    if (!isChatOpen) {
      return (
        // Hidden reCAPTCHA component that's always loaded but not visible
        <div className="hidden">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={siteKey}
            size="invisible"
            onChange={handleCaptchaChange}
            onError={handleCaptchaError}
            onExpired={handleCaptchaExpired}
          />
        </div>
      );
    }

    return (
      <Transition
        show={isChatOpen}
        enter="transition-all duration-300 ease-out"
        enterFrom="opacity-0 translate-y-4 scale-95"
        enterTo="opacity-100 translate-y-0 scale-100"
        leave="transition-all duration-200 ease-in"
        leaveFrom="opacity-100 translate-y-0 scale-100"
        leaveTo="opacity-0 translate-y-4 scale-95"
      >
        <div className="fixed bottom-4 left-4 z-40">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3 max-w-xs">
            <div className="flex items-center space-x-2">
              <ShieldCheckIcon className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                  Protected by reCAPTCHA
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {isLoading ? 'Verifying...' : 'Invisible protection active'}
                </p>
              </div>
            </div>
            
            {/* Hidden reCAPTCHA for invisible mode */}
            <div className="hidden">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={siteKey}
                size="invisible"
                onChange={handleCaptchaChange}
                onError={handleCaptchaError}
                onExpired={handleCaptchaExpired}
              />
            </div>
            
            {error && (
              <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                <p className="text-xs text-red-800 dark:text-red-200">{error}</p>
                <button
                  onClick={handleManualExecute}
                  className="mt-1 text-xs text-red-600 dark:text-red-400 hover:underline"
                  disabled={isLoading}
                >
                  Try again
                </button>
              </div>
            )}
          </div>
        </div>
      </Transition>
    );
  }

  // For visible reCAPTCHA, render the modal
  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Verify You're Human
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            aria-label="Close verification"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          To continue chatting, please complete the verification below to confirm you're not a robot.
        </p>

        {/* CAPTCHA */}
        <div className="flex justify-center mb-4">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={siteKey}
            onChange={handleCaptchaChange}
            onError={handleCaptchaError}
            onExpired={handleCaptchaExpired}
            theme="light" // You could make this dynamic based on dark mode
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center">
            <div className="inline-flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Verifying...
              </span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
          This verification helps protect our service from automated abuse.
        </div>
      </div>
    </div>
  );
}
